import React, { useState, useEffect } from 'react';
import { Activity, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import { Progress } from '../components/ui/progress';
import { healthAPI } from '../lib/api';
import { cn } from '../lib/utils';

export default function HealthPage() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await healthAPI.getRadar();
        setHealth(response.data);
      } catch (err) {
        console.error('Failed to fetch health data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-2 gap-6">
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
          <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'good':
        return 'bg-emerald-50 border-emerald-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'critical':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'good':
        return 'bg-emerald-500';
      case 'warning':
        return 'bg-amber-500';
      case 'critical':
        return 'bg-red-500';
      default:
        return 'bg-slate-400';
    }
  };

  // Prepare radar chart data
  const radarData = health?.metrics?.map((m) => ({
    subject: m.name.split(' ').slice(0, 2).join(' '),
    score: m.status === 'good' ? 100 : m.status === 'warning' ? 60 : 30,
    fullMark: 100,
  })) || [];

  return (
    <div className="space-y-6" data-testid="health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Business Health Radar</h2>
          <p className="text-slate-500">Real-time health indicators across key dimensions</p>
        </div>
        <div className={cn(
          'flex items-center gap-3 px-4 py-2 rounded-lg border',
          getStatusBg(health?.overall_status)
        )}>
          <Activity className="w-5 h-5" />
          <div>
            <p className="text-sm font-medium">Overall Health Score</p>
            <p className="text-2xl font-mono font-bold">{health?.overall_score}%</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Health Overview</h3>
          <ResponsiveContainer width="100%" height={350}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis
                dataKey="subject"
                tick={{ fontSize: 11, fill: '#64748B' }}
              />
              <PolarRadiusAxis
                angle={30}
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: '#64748B' }}
              />
              <Radar
                name="Health"
                dataKey="score"
                stroke="#DC5829"
                fill="#DC5829"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Metrics List */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Detailed Metrics</h3>
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {health?.metrics?.map((metric, index) => (
              <div
                key={index}
                className={cn(
                  'p-4 rounded-lg border',
                  getStatusBg(metric.status)
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(metric.status)}
                    <span className="font-medium text-slate-900">{metric.name}</span>
                  </div>
                  <span className="font-mono text-lg font-bold text-slate-900">
                    {metric.value}{metric.name.includes('%') || metric.name.includes('Rate') ? '%' : ''}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{metric.description}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Good: &lt;{metric.threshold_good}</span>
                  <span>•</span>
                  <span>Warning: &lt;{metric.threshold_warning}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <span className="font-medium text-emerald-900">Healthy</span>
          </div>
          <p className="text-3xl font-mono font-bold text-emerald-700">
            {health?.metrics?.filter((m) => m.status === 'good').length || 0}
          </p>
          <p className="text-sm text-emerald-600">metrics on track</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-900">Needs Attention</span>
          </div>
          <p className="text-3xl font-mono font-bold text-amber-700">
            {health?.metrics?.filter((m) => m.status === 'warning').length || 0}
          </p>
          <p className="text-sm text-amber-600">metrics to monitor</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-900">Critical</span>
          </div>
          <p className="text-3xl font-mono font-bold text-red-700">
            {health?.metrics?.filter((m) => m.status === 'critical').length || 0}
          </p>
          <p className="text-sm text-red-600">require immediate action</p>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Priority Actions</h3>
        <div className="space-y-3">
          {health?.metrics
            ?.filter((m) => m.status !== 'good')
            .sort((a, b) => (a.status === 'critical' ? -1 : 1))
            .slice(0, 5)
            .map((metric, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center',
                  metric.status === 'critical' ? 'bg-red-100' : 'bg-amber-100'
                )}>
                  <span className={cn(
                    'font-bold text-sm',
                    metric.status === 'critical' ? 'text-red-700' : 'text-amber-700'
                  )}>
                    {index + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{metric.name}</p>
                  <p className="text-sm text-slate-500">
                    Current: {metric.value} | Target: {metric.threshold_good}
                  </p>
                </div>
                <span className={cn(
                  'text-xs font-semibold px-2 py-1 rounded-full',
                  metric.status === 'critical'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {metric.status === 'critical' ? 'Urgent' : 'Monitor'}
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
